import { useSelector, useDispatch } from "react-redux"
import { createAsyncThunk, createSlice, isRejectedWithValue } from "@reduxjs/toolkit";
import { debounce } from "lodash";
import axiosObj from './axios';
import Client from "shopify-buy";

// Creates the client with Shopify-Buy v3.0 and store info
console.log('🔧 [Shopify Client] Initializing Shopify Buy client...');
console.log('🔧 [Shopify Client] Client import:', Client);
console.log('🔧 [Shopify Client] buildClient method available:', typeof Client.buildClient);

let client;
try {
  client = Client.buildClient({
    domain: "shop.nestingale.com",
    storefrontAccessToken: "aacf6bb4a693f1477688cb1098928f9b",
    apiVersion: "2024-01" // Use latest stable API version
  });
  console.log('✅ [Shopify Client] Client created successfully:', client);
  console.log('🔧 [Shopify Client] Client.checkout available:', typeof client?.checkout);
  console.log('🔧 [Shopify Client] Client.checkout.create available:', typeof client?.checkout?.create);
  console.log('📋 [Shopify Client] Note: v3.0 uses existing .checkout interface but Cart API under the hood');
  
  // Expose to window for debugging
  if (typeof window !== 'undefined') {
    window.shopifyClient = client;
    window.shopifyClientDebug = {
      hasClient: !!client,
      hasCheckout: !!client?.checkout,
      hasCheckoutCreate: !!client?.checkout?.create,
      hasCheckoutFetch: !!client?.checkout?.fetch,
      hasCheckoutAddLineItems: !!client?.checkout?.addLineItems,
      hasCheckoutUpdateLineItems: !!client?.checkout?.updateLineItems,
      hasCheckoutRemoveLineItems: !!client?.checkout?.removeLineItems,
      hasCheckoutUpdateEmail: !!client?.checkout?.updateEmail,
      hasCheckoutUpdateShippingAddress: !!client?.checkout?.updateShippingAddress,
      clientMethods: Object.keys(client || {}),
      checkoutMethods: Object.keys(client?.checkout || {})
    };
    console.log('🪟 [Shopify Client] Exposed to window.shopifyClient and window.shopifyClientDebug');
  }
} catch (error) {
  console.error('❌ [Shopify Client] Failed to create client:', error);
  client = null;
}



const initialState = {
  cartCount: 0,
  checkout: {},
  guestCheckout: {},
  products: [],
  featured: [],
  product: {},
  shop: {},
  dbAddress: {},
  createNewCart: false
};

export const getProducts = createAsyncThunk(
  "shopify/getProductsData",
  async (config, { rejectWithValue }) => {
	try{
		window.nestingale.apiTriggered=true;
		const products = await client.product.fetchAll();
		return products.map((product) => ({
		id: product.id,
		name: product.title,
		description: product.description,
		price: {
			amount: product.variants[0].price.amount,
			currencyCode: product.variants[0].price.currencyCode,
			type: product.variants[0].price.type,
		},
		image: product.images[0].src,
		}));
	}catch(e){
		return rejectWithValue(e);
	}finally{
		window.nestingale.apiTriggered=false;
	}
  }
);

export const getProduct = createAsyncThunk(
  "shopify/getProductData",
  async (id, { rejectWithValue }) => {
	try{
		window.nestingale.apiTriggered=true;
		return client.product.fetch(id);
	}catch(e){
		return rejectWithValue(e);
	}finally{
		window.nestingale.apiTriggered=false;
	}
  }
);

export const checkout = createAsyncThunk(
  "shopify/checkoutData",
  async (config, { rejectWithValue }) => {
	try{
		console.log('🛒 [Cart API v3.0] Creating/Fetching checkout (now using Cart API under the hood)...', config);
		
		// Check if client exists and has checkout methods
		if (!client) {
			console.error('❌ [Cart API v3.0] Shopify client is null/undefined');
			return rejectWithValue(new Error('Shopify client not initialized'));
		}
		
		if (!client.checkout) {
			console.error('❌ [Cart API v3.0] Shopify client.checkout is undefined');
			console.error('🔧 [Cart API v3.0] Available client methods:', Object.keys(client));
			return rejectWithValue(new Error('Shopify client.checkout not available'));
		}
		
		window.nestingale.apiTriggered=true;
		let checkoutId = config.checkoutId || config.cartId;
		if(!checkoutId){
			console.log('🛒 [Cart API v3.0] No existing checkout ID, creating new checkout...');
			// In v3.0, checkout.create() now uses Cart API under the hood
			const checkoutResponse = await client.checkout.create();
			checkoutId = checkoutResponse.id;
			console.log('✅ [Cart API v3.0] New checkout created successfully (Cart API):', checkoutId);
		} else {
			console.log('🛒 [Cart API v3.0] Using existing checkout ID:', checkoutId);
		}
    	// Get the checkout details using the checkout ID
		console.log('🛒 [Cart API v3.0] Fetching checkout details for ID:', checkoutId);
    	const checkoutDetailsResponse = await client.checkout.fetch(checkoutId);
		console.log('✅ [Cart API v3.0] Checkout details fetched successfully (Cart API):', {
			id: checkoutDetailsResponse.id,
			lineItemsCount: checkoutDetailsResponse.lineItems?.length || 0,
			totalQuantity: checkoutDetailsResponse.totalQuantity || 0,
			totalPrice: checkoutDetailsResponse.totalPrice?.amount || 0,
			webUrl: checkoutDetailsResponse.webUrl
		});
		//convert the GraphQL object to normal JSON
    	return JSON.parse(JSON.stringify(checkoutDetailsResponse));
	}catch(e){
		console.error('❌ [Cart API v3.0] Error in checkout operation:', e);
		return rejectWithValue(e);
	}finally{
		window.nestingale.apiTriggered=false;
	}
  }
);

export const shopInfo = createAsyncThunk(
  "shopify/shopInfoData",
  async (config, { rejectWithValue }) => {
	try{
		window.nestingale.apiTriggered=true;
    	return client.shop.fetchInfo();
	}catch(e){
		return rejectWithValue(e);
	}finally{
		window.nestingale.apiTriggered=false;
	}
  }
);


export const recreateCheckoutAndAddVariants = createAsyncThunk(
	"shopify/recreateCheckoutAndAddVariantsData",
	async ({ checkoutId, lineItemsToAdd, successCallback }, { rejectWithValue }) => {
	  try{
		console.log('🔄 [Cart API v3.0] Recreating checkout and adding variants (now using Cart API)...', {
			oldCheckoutId: checkoutId,
			itemsToAdd: lineItemsToAdd?.length || 0
		});
		window.nestingale.apiTriggered=true;
		// In v3.0, checkout.create() now uses Cart API under the hood
		const checkoutResponse = await client.checkout.create();
		console.log('✅ [Cart API v3.0] New checkout created for recreation (Cart API):', checkoutResponse.id);
		const lineItemsToAddInNewCart = lineItemsToAdd.filter((item) => item);
		console.log('🛒 [Cart API v3.0] Filtered line items to add:', lineItemsToAddInNewCart);
		
		const response = await client.checkout.addLineItems(
			checkoutResponse.id,
			lineItemsToAddInNewCart
		);
		console.log('✅ [Cart API v3.0] Variants added to recreated checkout (Cart API):', {
			checkoutId: response.id,
			addedItems: response.lineItems?.length || 0,
			totalQuantity: response.totalQuantity || 0
		});
		return JSON.parse(JSON.stringify(response));
	  }catch(e){
		  console.error('❌ [Cart API v3.0] Error recreating checkout and adding variants:', e);
		  return rejectWithValue(e);
	  }finally{
		window.nestingale.apiTriggered=false;
	  }
	}
  );

export const addVariantToCart = createAsyncThunk(
	"shopify/addVariantToCartData",
	async ({ checkoutId, lineItemsToAdd, successCallback }, { rejectWithValue }) => {
		try{
			console.log('➕ [Cart API v3.0] Adding variants to checkout (now using Cart API)...', {
				checkoutId: checkoutId,
				itemsToAdd: lineItemsToAdd?.length || 0,
				items: lineItemsToAdd
			});
			window.nestingale.apiTriggered=true;
			const lineItemsToAddInNewCart = lineItemsToAdd.filter((item) => item);
			console.log('🛒 [Cart API v3.0] Filtered items for addition:', lineItemsToAddInNewCart);

			// In v3.0, checkout.addLineItems() now uses Cart API under the hood
			const response = await client.checkout.addLineItems(
				checkoutId,
				lineItemsToAddInNewCart
			  );
			console.log('✅ [Cart API v3.0] Variants added successfully (Cart API):', {
				checkoutId: response.id,
				totalItems: response.lineItems?.length || 0,
				totalQuantity: response.totalQuantity || 0,
				totalPrice: response.totalPrice?.amount || 0
			});
			return JSON.parse(JSON.stringify(response));
		}catch(e){
			console.error('❌ [Cart API v3.0] Error adding variants to checkout:', e);
			return rejectWithValue(e);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	}
  );
  
  export const updateQuantityInCart = createAsyncThunk(
	"shopify/updateQuantityInCartData",
	async ({ lineItemId, quantity, checkoutID }, { rejectWithValue }) => {

		try{
			console.log('🔄 [Cart API v3.0] Updating item quantity (now using Cart API)...', {
				checkoutId: checkoutID,
				lineItemId: lineItemId,
				newQuantity: quantity
			});
			window.nestingale.apiTriggered=true;
			const lineItemsToUpdate = [
				{ id: lineItemId, quantity: parseInt(quantity, 10) },
			  ];
			console.log('🛒 [Cart API v3.0] Line items to update:', lineItemsToUpdate);
			  // In v3.0, checkout.updateLineItems() now uses Cart API under the hood
			  const resp = await client.checkout.updateLineItems(
				checkoutID,
				lineItemsToUpdate
			  );
			console.log('✅ [Cart API v3.0] Quantity updated successfully (Cart API):', {
				checkoutId: resp.id,
				totalItems: resp.lineItems?.length || 0,
				totalQuantity: resp.totalQuantity || 0
			});
			  return JSON.parse(JSON.stringify(resp));
		}catch(e){
			console.error('❌ [Cart API v3.0] Error updating quantity:', e);
			return rejectWithValue(e);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	  
	}
  );

  export const updateQuantitiesInCart = createAsyncThunk(
	"shopify/updateQuantitiesInCartData",
	async ({ lineItemsToUpdate, checkoutID }, { rejectWithValue }) => {

		try{
			console.log('🔄 [Cart API v3.0] Updating multiple quantities (now using Cart API)...', {
				checkoutId: checkoutID,
				itemsToUpdate: lineItemsToUpdate?.length || 0,
				updates: lineItemsToUpdate
			});
			window.nestingale.apiTriggered=true;
			// In v3.0, checkout.updateLineItems() now uses Cart API under the hood
			const resp = await client.checkout.updateLineItems(
				checkoutID,
				lineItemsToUpdate
			);
			console.log('✅ [Cart API v3.0] Multiple quantities updated successfully (Cart API):', {
				checkoutId: resp.id,
				totalItems: resp.lineItems?.length || 0,
				totalQuantity: resp.totalQuantity || 0,
				totalPrice: resp.totalPrice?.amount || 0
			});
			return JSON.parse(JSON.stringify(resp));
		}catch(e){
			console.error('❌ [Cart API v3.0] Error updating multiple quantities:', e);
			return rejectWithValue(e);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	  
	}
  );

  export const removeLineItemInCart = createAsyncThunk(
	"shopify/removeLineItemInCartData",
	async ({ checkoutID, lineItemId }, { rejectWithValue }) => {
		try{
			console.log('🗑️ [Cart API v3.0] Removing item from checkout (now using Cart API)...', {
				checkoutId: checkoutID,
				lineItemId: lineItemId
			});
			window.nestingale.apiTriggered=true;
			// In v3.0, checkout.removeLineItems() now uses Cart API under the hood
			const resp = await client.checkout.removeLineItems(checkoutID, [lineItemId]);
			console.log('✅ [Cart API v3.0] Item removed successfully (Cart API):', {
				checkoutId: resp.id,
				remainingItems: resp.lineItems?.length || 0,
				totalQuantity: resp.totalQuantity || 0
			});
	  		return JSON.parse(JSON.stringify(resp));
		}catch(e){
			console.error('❌ [Cart API v3.0] Error removing item from checkout:', e);
			return rejectWithValue(e);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	  
	}
  );

  export const updateEmailAddressInShopify = createAsyncThunk(
	"shopify/updateEmailAddressInShopifyData",
	async ({ checkoutId, emamilAddress }, { rejectWithValue }) => {
	    if (!emamilAddress) {
			console.log('⚠️ [Cart API v3.0] No email address provided, skipping update');
			return;
		}
		try{
			console.log('📧 [Cart API v3.0] Updating email address (now using Cart API)...', {
				checkoutId: checkoutId,
				email: emamilAddress
			});
			window.nestingale.apiTriggered=true;
			// In v3.0, checkout.updateEmail() now uses Cart API under the hood
			const response = await client.checkout.updateEmail(
				checkoutId,
				emamilAddress
			  );
			console.log('✅ [Cart API v3.0] Email address updated successfully (Cart API):', {
				checkoutId: response.id,
				email: response.email
			});
			  return JSON.parse(JSON.stringify(response));
		}catch(e){
			console.error('❌ [Cart API v3.0] Error updating email address:', e);
			return rejectWithValue(e);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	}
  );

  
  export const updateShippingAddressInShopify = createAsyncThunk(
	"shopify/updateShippingAddressInShopifyData",
	async ({ checkoutId,  shippingAddress }, { rejectWithValue }) => {
		try{
			if(!shippingAddress["addressList"] || shippingAddress["addressList"].length == 0) {
				console.log("⚠️ [Cart API v3.0] No addressList for this user");
				return;
			}
			console.log('🏠 [Cart API v3.0] Updating shipping address (now using Cart API)...', {
				checkoutId: checkoutId,
				addressCount: shippingAddress.addressList?.length || 0
			});
			window.nestingale.apiTriggered=true;
			const defaultTrueAddress  = shippingAddress.addressList.filter((eachAddress) => eachAddress.default)

			let latestAddress  = defaultTrueAddress[0];
			console.log('🏠 [Cart API v3.0] Using default address:', {
				firstName: latestAddress.first_name,
				lastName: latestAddress.last_name,
				city: latestAddress.city,
				country: latestAddress.country
			});
			const shippingAddressToSend = {
				address1: latestAddress.address1,
				address2: latestAddress.address2,
				city: latestAddress.city,
				company: latestAddress.company,
				country: latestAddress.country,
				firstName: latestAddress.first_name,
				lastName: latestAddress.last_name,
				phone: latestAddress.phone,
				province: latestAddress.province,
				zip: latestAddress.zip 
			  };
			 
			console.log('🏠 [Cart API v3.0] Shipping address payload:', shippingAddressToSend);
			// In v3.0, checkout.updateShippingAddress() now uses Cart API under the hood
			const response = await client.checkout.updateShippingAddress(checkoutId, shippingAddressToSend);
			console.log('✅ [Cart API v3.0] Shipping address updated successfully (Cart API):', {
				checkoutId: response.id,
				shippingAddress: response.shippingAddress
			});
			return JSON.parse(JSON.stringify(response));
		}catch(e){
			console.error('❌ [Cart API v3.0] Error updating shipping address:', e);
			return rejectWithValue(e);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	}
  );

  export const getShippingAddressInShopify = createAsyncThunk(
	"shopify/getShippingAddressInShopifyData",
	async ({ customerId , brand}, { rejectWithValue }) => {
		try {
			const headers =  {
				'content-type': "application/json"
			}
			let url  = `/api/customer/addresses?userId=${customerId}` 
			if(brand && brand !== 'nestingale'){
				url += "&vendor="+ brand 
			}
			window.nestingale.apiTriggered=true;
			const addressData = await axiosObj.get(url)
				.then((response) => {
					return response.data
				},(err)=>{
					const errorObj = Object.assign({description: "NEST_SPA:Slices:getShippingAddressInShopify:Exception", errorCode: "NEST_SPA:HOME_PAGE"},err)
					console.log(errorObj);
					return rejectWithValue(err);
					//return welcomeProfile;
				});

				console.log("addressData=",addressData)
			return addressData;
		} catch (err) {
			console.log({description: "NEST_SPA:Slices:getShippingAddressInShopify:Exception", errorCode: "NEST_SPA:HOME_PAGE", err_desc: err.message, err_stack: err.stack});
			return rejectWithValue(err);
		}finally{
			window.nestingale.apiTriggered=false;
		}
	}
  ); 


const shopifySlice = createSlice({
  name: "shopify",
  initialState,
  reducers: {
    handleSetCount: (state, action) => {
      state.cartCount = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.fulfilled, (state, action) => {
        state.products = action.payload;
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        state.product = action.payload;
      })
      .addCase(checkout.fulfilled, (state, action) => {
		console.log('✅ [Cart API v3.0] Checkout state updated (now using Cart API):', {
			isGuest: !!action.meta.arg.guest,
			checkoutId: action.payload?.id,
			itemCount: action.payload?.lineItems?.length || 0,
			totalQuantity: action.payload?.totalQuantity || 0
		});
		if(action.meta.arg.guest){
			state.guestCheckout = action.payload;
		}else{
			state.checkout = action.payload;
			if(!state.checkout){
				state.createNewCart=true;
				console.log('⚠️ [Cart API v3.0] No checkout state, marking for new cart creation');
			}
		}
      })
      .addCase(shopInfo.fulfilled, (state, action) => {
        state.shop = action.payload;
      })
      .addCase(addVariantToCart.fulfilled, (state, {meta, payload}) => {
		console.log('✅ [Cart API v3.0] Variants added to checkout state (now using Cart API):', {
			checkoutId: payload?.id,
			itemCount: payload?.lineItems?.length || 0,
			totalQuantity: payload?.totalQuantity || 0,
			hasSuccessCallback: !!meta.arg.successCallback
		});
        state.checkout = payload;
		checkAndNavigateToCart(meta.arg.successCallback);
      })
	  .addCase(recreateCheckoutAndAddVariants.fulfilled, (state, {meta, payload}) => {
		console.log('✅ [Cart API v3.0] Checkout recreated and variants added (now using Cart API):', {
			checkoutId: payload?.id,
			itemCount: payload?.lineItems?.length || 0,
			totalQuantity: payload?.totalQuantity || 0,
			updateDBFlag: true
		});
        state.checkout = payload;
		state.updateDB=true;
		checkAndNavigateToCart(meta.arg.successCallback);
      })

	  

      .addCase(updateQuantityInCart.fulfilled, (state, action) => {
        state.checkout = action.payload;
      })
	  .addCase(updateQuantitiesInCart.fulfilled, (state, action) => {
        state.checkout = action.payload;
      })


	  
      .addCase(removeLineItemInCart.fulfilled, (state, action) => {
        state.checkout = action.payload;
      })
	  .addCase(getShippingAddressInShopify.fulfilled, (state, action) => {
        state.dbAddress = action.payload;
      });
  },
});

export const {
  handleSetCount
} = shopifySlice.actions;

function checkAndNavigateToCart(callback) {
	if(callback){
		callback(true);
	}
}

export function useShopify() {
	const dispatch = useDispatch()
	const cartStatus = useSelector((state) => true)
	const cartCount = useSelector((state) => state.shopifyReducer.cartCount)
	const products = useSelector((state) => state.shopifyReducer.products)
	const product = useSelector((state) => state.shopifyReducer.product)
	const featured = useSelector((state) => state.shopifyReducer.featured)
	const guestCheckoutState = useSelector((state) => state.shopifyReducer.guestCheckout);
	const checkoutState = useSelector(
		(state) => state.shopifyReducer.checkout
	)



	const cartQuantity = useSelector(
		(state) => {
			let checkoutState = state.shopifyReducer.checkout
		
			if(checkoutState && checkoutState.lineItems){
				const latestCount = checkoutState.lineItems.reduce((currentValue, eachItem) => {
					currentValue += (eachItem.quantity || 0);
					return currentValue;
				}, 0);

				return latestCount
			}
			return 0;
		}
	)

	const skusCommaSeperated = useSelector(
		(state) => {
			let checkoutState = state.shopifyReducer.checkout
		
			if(checkoutState && checkoutState.lineItems){
				let skuArr = checkoutState.lineItems.map((lineItem, i) => {
					if (lineItem.variant && lineItem.variant.sku){
						return lineItem.variant.sku
					}
				})

				return skuArr
			}
		}
	)
	
	const createNewCart = useSelector(
		(state) => state.shopifyReducer.createNewCart
	)
	const checkoutID = useSelector(
		(state) => state.shopifyReducer?.checkout?.id
	)
	
	const updateDB = useSelector(
		(state) => state.shopifyReducer.updateDB
	)
	

	const dbAddressInfo =  useSelector((state)=> state.shopifyReducer.dbAddress)
	const shopDetails = useSelector((state) => state.shopifyReducer.shop)
	const fetchProducts = () => dispatch(getProducts())
	const fetchProduct = (id) => dispatch(getProduct(id))
	// const fetchCollection = () => dispatch(getCollection())
	const createCheckout = (config) => dispatch(checkout(config))
	const createShop = () => dispatch(shopInfo())
	const setCount = (count) => dispatch(handleSetCount(count))
    
	// Enhanced getClient for debugging
	const getClient = () => {
		console.log('🔧 [Shopify Client] getClient called, client status:', {
			hasClient: !!client,
			hasCheckout: !!client?.checkout,
			clientType: typeof client,
			checkoutType: typeof client?.checkout,
			note: 'v3.0 uses .checkout interface with Cart API under the hood'
		});
		return client;
	}
	
	const addVariant = (checkoutId, lineItemsToAdd, successCallback) =>
		dispatch(addVariantToCart({ checkoutId, lineItemsToAdd, successCallback }))
	const createNewCheckOutAndaddVariant = (checkoutId, lineItemsToAdd, successCallback) =>
		dispatch(recreateCheckoutAndAddVariants({ checkoutId, lineItemsToAdd, successCallback }))


	
	const updateQuantities = (lineItemsToUpdate, checkoutID) =>
		 dispatch(updateQuantitiesInCart({lineItemsToUpdate, checkoutID}))
	const updateQuantity = (lineItemId, quantity, checkoutID) =>
		dispatch(updateQuantityInCart({lineItemId, quantity, checkoutID}))
	const removeLineItem = (checkoutID, lineItemId) =>
		dispatch(removeLineItemInCart({checkoutID, lineItemId}))

    const fetchShippingAddressInShopify = (postData) => {
		dispatch(getShippingAddressInShopify(postData));
	}

	const saveShippingAddressInShopify = (postData) => {
		dispatch(updateShippingAddressInShopify(postData));
	}

	const saveEmailAddressInShopify = (postData) => dispatch(updateEmailAddressInShopify(postData));

	return {
		getClient,
		createNewCart,
		products,
		product,
		featured,
		cartStatus,
		checkoutState,
		skusCommaSeperated,
		guestCheckoutState,
		cartCount,
		shopDetails,
		updateDB,
		cartQuantity,
		updateQuantities,
		addVariant,
		createNewCheckOutAndaddVariant,
		fetchProducts,
		fetchProduct,
		fetchShippingAddressInShopify,
		saveShippingAddressInShopify,
		saveEmailAddressInShopify,
		checkoutID,
		// fetchCollection,
		createCheckout,
		createShop,
		updateQuantity,
		removeLineItem,
		setCount,
		dbAddressInfo
	}
}


export default shopifySlice.reducer;

// // Creates the client with Shopify-Buy and store info
// const client = Client.buildClient({
// 	domain: '178f33-2.myshopify.com',
// 	storefrontAccessToken: 'aacf6bb4a693f1477688cb1098928f9b'
// });

// const PRODUCTS_FOUND = "shopify/PRODUCTS_FOUND"
// const PRODUCT_FOUND = "shopify/PRODUCT_FOUND"
// const COLLECTION_FOUND = "shopify/COLLECTION_FOUND"
// const CHECKOUT_FOUND = "shopify/CHECKOUT_FOUND"
// const SHOP_FOUND = "shopify/SHOP_FOUND"
// const ADD_VARIANT_TO_CART = "shopify/ADD_VARIANT_TO_CART"
// const UPDATE_QUANTITY_IN_CART = "shopify/UPDATE_QUANTITY_IN_CART"
// const REMOVE_LINE_ITEM_IN_CART = "shopify/REMOVE_LINE_ITEM_IN_CART"

// const CART_COUNT = "shopify/CART_COUNT"

// const initialState = {
// 	cartCount: 0,
// 	checkout: {},
// 	products: [],
// 	featured: [],
// 	product: {},
// 	shop: {},
// }

// const shopifyReducer = (state = initialState, action) => {

// 	switch (action.type) {
// 		case PRODUCTS_FOUND:
// 			debugger
// 			return { ...state, products: [{"a":"hello"}] } ;break;
// 		case PRODUCT_FOUND:
// 			return { ...state, product: action.payload }
// 		case COLLECTION_FOUND:
// 			return { ...state, featured: action.payload }
// 		case CHECKOUT_FOUND:
// 			return { ...state, checkout: action.payload }
// 		case SHOP_FOUND:
// 			return { ...state, shop: action.payload }
// 		case ADD_VARIANT_TO_CART:
// 			return { ...state, checkout: action.payload }
// 		case UPDATE_QUANTITY_IN_CART:
// 			return { ...state, checkout: action.payload }
// 		case REMOVE_LINE_ITEM_IN_CART:
// 			return { ...state, checkout: action.payload }
// 		case OPEN_CART:
// 			return { ...state, isCartOpen: true }
// 		case CLOSE_CART:
// 			return { ...state, isCartOpen: false }
// 		case CART_COUNT:
// 			return { ...state, cartCount: action.payload }
// 		default:
// 			debugger
// 			return state
// 	}
// }

// // Gets all the products from Shopify
// function getProducts() {
// 	return (dispatch) => {
// 		client.product.fetchAll().then((products) => {
			
// 			const transformedProducts = products.map((product) => {
				
// 				// Access product properties using the provided methods
// 				const productId = product.id;
// 				const productName = product.title;
// 				const productDescription = product.description;
// 				const productPrice = product.variants[0].price;
// 				const productImage = product.images[0].src;

// 				 // Extract properties from the price object
// 				 const priceAmount = productPrice.amount;
// 				 const priceCurrencyCode = productPrice.currencyCode;
// 				 const priceType = productPrice.type;
			
// 				// Return the extracted information as an object
// 				return {
// 				  id: productId,
// 				  name: productName,
// 				  description: productDescription,
// 				  price: {
// 					amount: priceAmount,
// 					currencyCode: priceCurrencyCode,
// 					type: priceType,
// 				  },
// 				  image: productImage,
// 				};
// 			  });
// 			  debugger
// 			dispatch({
// 				type: PRODUCTS_FOUND,
// 				payload: transformedProducts,
// 			})
// 		})
// 	}
// }

// // Gets individual item based on id
// function getProduct(id) {
// 	return async (dispatch) => {
// 		const resp = await client.product.fetch(id)
// 		dispatch({
// 			type: PRODUCT_FOUND,
// 			payload: resp,
// 		})
// 		return resp
// 	}
// }

// //
// // Gets a  collection based on that collection's id
// //
// // function getCollection() {
// // 	const collectionId = "Z2lkOi8vc2hvcGlmeS9Db2xsZWN0aW9uLzIwODAyMDYwMzAzMw=="
// // 	return (dispatch) => {
// // 		client.collection.fetchWithProducts(collectionId).then((resp) => {
// // 			dispatch({
// // 				type: COLLECTION_FOUND,
// // 				payload: resp.products,
// // 			})
// // 		})
// // 	}
// // }

// // Creates initial checkout state from Shopify
// function checkout() {
// 	return (dispatch) => {
// 		client.checkout.create().then((resp) => {
// 			dispatch({
// 				type: CHECKOUT_FOUND,
// 				payload: resp,
// 			})
// 		})
// 	}
// }

// // Gets Shopify store information
// function shopInfo() {
// 	return (dispatch) => {
// 		client.shop.fetchInfo().then((resp) => {
// 			dispatch({
// 				type: SHOP_FOUND,
// 				payload: resp,
// 			})
// 		})
// 	}
// }

// // Adds variants to cart/checkout
// function addVariantToCart(checkoutId, lineItemsToAdd) {
// 	return async (dispatch) => {
// 		const response = await client.checkout.addLineItems(
// 			checkoutId,
// 			lineItemsToAdd
// 		)
// 		dispatch({
// 			type: ADD_VARIANT_TO_CART,
// 			payload: response,
// 		})
// 		return response
// 	}
// }

// // Updates quantity of line items in cart and in checkout state
// function updateQuantityInCart(lineItemId, quantity, checkoutId) {
// 	const lineItemsToUpdate = [
// 		{ id: lineItemId, quantity: parseInt(quantity, 10) },
// 	]

// 	return async (dispatch) => {
// 		const resp = await client.checkout.updateLineItems(
// 			checkoutId,
// 			lineItemsToUpdate
// 		)
// 		dispatch({
// 			type: UPDATE_QUANTITY_IN_CART,
// 			payload: resp,
// 		})
// 		return resp
// 	}
// }

// // Removes line item from cart and checkout state
// function removeLineItemInCart(checkoutId, lineItemId) {
// 	return (dispatch) => {
// 		client.checkout.removeLineItems(checkoutId, [lineItemId]).then((resp) => {
// 			dispatch({
// 				type: REMOVE_LINE_ITEM_IN_CART,
// 				payload: resp,
// 			})
// 		})
// 	}
// }



// Set the count of items in the cart
// function handleSetCount(count) {
// 	return {
// 		type: CART_COUNT,
// 		payload: count,
// 	}
// }


